import { Checkbox, Chip, LazyLoader, Modal, Select, type SelectOptionType, Switch } from '@bp/ui-components';
import { useLongPress } from '@uidotdev/usehooks';
import classNames from 'classnames';
import { BpTipTap } from 'components/BpTipTap/BpTipTap';
import dayjs, { type Dayjs } from 'dayjs';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';
import { useWindowDimensions } from 'utils/dimensions';
import { isNumber } from '../../../../utils/type-guards';
import { ModalBottomButtons } from '../../../ModalBottomButtons/ModalBottomButtons';
import styles from './ClassbookAttendeeListItem.module.scss';

export type AbsenceType = {
  uuid: string | null;
  attendeeUuid: string;
  absentFrom: Dayjs;
  absentUntil: Dayjs;
  absenceReason: string | null;
  excused: boolean;
  status: 'present' | 'absent' | 'excused' | 'wholeDay';
  lateInMinutes?: number | null;
};

export type Attendee = {
  uuid: string;
  name: string;
  comment?: string | null;
  lateInMinutes?: number | null;
  absence?: AbsenceType | null;
};

type ClassbookAttendeeListItemProps = {
  attendee: Attendee;
  reasons: SelectOptionType[];
  start: Dayjs;
  end: Dayjs;
  onAbsenceUpdate: (updatedAbsence: Partial<AbsenceType> & { comment?: string }) => Promise<void>;
  className?: string | undefined;
};

export const ClassbookAttendeeListItem = ({
  attendee,
  start,
  end,
  reasons,
  onAbsenceUpdate,
  className,
}: ClassbookAttendeeListItemProps) => {
  const { t } = useTranslation();

  const { isPhone } = useWindowDimensions();

  const [pendingValues, setPendingValues] = useState<(Partial<AbsenceType> & { comment?: string }) | null>(null);
  const [updating, setUpdating] = useState(false);

  const [isExpanded, setIsExpanded] = useState(false);
  const [showComment, setShowComment] = useState<boolean>(!!attendee.comment);
  const [showLate, setShowLate] = useState<boolean>(!!attendee.lateInMinutes);
  const [showLateModal, setShowLateModal] = useState<boolean>(false);

  const [longPressActive, setLongPressActive] = useState(false);
  const [longPressProcess, setLongPressProcess] = useState(0);

  const resetLongPress = () => {
    setLongPressProcess(0);
    setLongPressActive(false);
  };

  const attrs = useLongPress(
    () => {
      setShowLateModal(true);
      resetLongPress();
    },
    {
      onStart: () => {
        setLongPressProcess(0);
        setLongPressActive(true);
      },
      onFinish: resetLongPress,
      onCancel: resetLongPress,
      threshold: 1000,
    },
  );

  useEffect(() => {
    if (longPressActive) {
      let process = 0;
      const timer = setInterval(() => {
        process += 5;
        setLongPressProcess(process);
        if (process >= 100) {
          clearInterval(timer);
        }
      }, 50);
      return () => clearInterval(timer);
    }
  }, [longPressActive]);

  function getStatus(): ReactNode {
    if (updating && !isExpanded) {
      return <LazyLoader embedded transparent={true} className={styles.loader} />;
    }
    let text = '';
    let bgColor = '';
    switch (values.status) {
      case 'present':
        text = t('classbook.present');
        bgColor = 'var(--color-success)';
        break;
      case 'excused':
        text = t('classbook.excused');
        bgColor = 'var(--color-warning)';
        break;
      case 'absent':
        text = t('classbook.absent');
        bgColor = 'var(--color-error)';
        break;
      case 'wholeDay':
        text = t('classbook.wholeDayAbsent');
        bgColor = 'var(--color-error)';
        break;
      default:
        text = t('classbook.present');
        bgColor = 'var(--color-success)';
    }
    if (values.comment) {
      text += `/${t('common.comment')}`;
    }

    return <Chip value={text} bgColor={bgColor} maxWidth={'200px'} color='var(--color-white)' />;
  }

  const values = useMemo(() => {
    const baseValues = {
      attendeeUuid: attendee.uuid,
      uuid: attendee.absence?.uuid,
      absentFrom: dayjs(attendee.absence?.absentFrom ?? start),
      absentUntil: dayjs(attendee.absence?.absentUntil ?? end),
      absenceReason: (reasons.find((r) => r.value === attendee.absence?.absenceReason)?.value as string) ?? null,
      comment: attendee.comment ?? '',
      status: attendee.absence?.status ?? 'present',
      excused: attendee.absence?.excused,
      lateInMinutes: attendee.lateInMinutes ?? null,
    };

    return pendingValues ? { ...baseValues, ...pendingValues } : baseValues;
  }, [attendee, end, reasons, start, pendingValues]);

  const lateOptions = useMemo(() => {
    const options: SelectOptionType[] = [];
    const totalMinutes = end.diff(start, 'minute');
    for (let i = 5; i <= totalMinutes; i += 5) {
      const lateTime = start.add(i, 'minute');
      options.push({
        value: i,
        label: `${t('common.minutes', { count: i })} (${lateTime.format('HH:mm')} Uhr)`,
      });
    }
    return options;
  }, [start, end, t]);

  const calculateDefaultLateMinutes = () => {
    const now = dayjs();
    const diff = Math.floor(now.diff(start, 'minute') / 5) * 5;
    const totalMinutes = end.diff(start, 'minute');

    if (diff > 0 && diff < totalMinutes && now.isSame(start, 'day')) {
      return diff;
    }
    return 5;
  };

  const handleExcusedChange = (checked: boolean) => {
    setPendingValues({
      ...values,
      status: checked ? 'excused' : 'absent',
    });
    handleUpdate({
      ...attendee.absence,
      excused: checked,
    });
  };

  const handleLateToggle = (enabled: boolean) => {
    if (enabled) {
      const defaultMinutes = calculateDefaultLateMinutes();
      setPendingValues({ ...values, lateInMinutes: defaultMinutes });
      handleUpdate({ ...attendee.absence, lateInMinutes: defaultMinutes });
    } else {
      setPendingValues({ ...values, lateInMinutes: null });
      handleUpdate({ ...attendee.absence, lateInMinutes: null });
    }
    setShowLate(enabled);
  };

  const handleCommentToggle = (enabled: boolean) => {
    setShowComment(enabled);
    if (!enabled) {
      setPendingValues({ ...values, comment: '' });
      handleUpdate({ ...attendee.absence, comment: '' });
    }
  };

  const handleStatusCycle = () => {
    if (updating) return;

    const statusTransitions: Record<
      string,
      { status: AbsenceType['status']; absentFrom: Dayjs; absentUntil: Dayjs; lateInMinutes: number | null }
    > = {
      present: { status: 'absent' as const, absentFrom: start, absentUntil: end, lateInMinutes: null },
      absent: {
        status: 'absent' as const,
        absentFrom: dayjs().startOf('day'),
        absentUntil: dayjs().endOf('day'),
        lateInMinutes: null,
      },
      wholeDay: {
        status: 'present' as const,
        absentFrom: start,
        absentUntil: end,
        lateInMinutes: values.lateInMinutes,
      },
    };

    const transition = statusTransitions[values.status];
    if (transition) {
      const newStatus = values.status === 'absent' ? 'wholeDay' : transition.status;
      const lateInMinutes = newStatus === 'present' ? values.lateInMinutes : null;
      if (newStatus !== 'present') {
        setShowLate(false);
      }
      setPendingValues({ ...values, status: newStatus, lateInMinutes });
      handleUpdate({
        uuid: attendee.absence?.uuid,
        ...transition,
      });
    }
  };

  const optionsClasses = classNames(styles.options, {
    [styles['is-expanded']]: isExpanded,
    [styles['is-absent']]: values.status !== 'present',
    [styles['has-comment']]: showComment,
  });

  const renderExcusedCheckbox = (additionalClassName?: string) => (
    <Checkbox
      className={classNames(styles.excused, additionalClassName)}
      name={`excused-${attendee.uuid}`}
      checked={attendee.absence?.excused ?? false}
      label={t('classbook.excused')}
      onChange={(e) => handleExcusedChange(e.target.checked)}
    />
  );

  async function handleUpdate(updatedValues: (Partial<AbsenceType> & { comment?: string }) | null = null) {
    setUpdating(true);
    await onAbsenceUpdate({
      attendeeUuid: attendee.uuid,
      uuid: updatedValues?.uuid,
      absentFrom: updatedValues?.absentFrom,
      absentUntil: updatedValues?.absentUntil,
      absenceReason: updatedValues?.absenceReason,
      comment: updatedValues?.comment,
      status: updatedValues?.status,
      excused: updatedValues?.excused,
      lateInMinutes: updatedValues?.lateInMinutes,
    });
    setUpdating(false);
  }

  const itemHeaderClasses = classNames(styles['item-header'], {
    [styles.updating]: longPressActive,
  });

  return (
    <>
      <div
        className={classNames(styles['classbook-attendee-list-item'], className)}
        onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
      >
        <div
          className={itemHeaderClasses}
          onClick={() => setIsExpanded(!isExpanded)}
          onTouchStart={values.status === 'present' && !showLate ? attrs.onTouchStart : undefined}
          onTouchEnd={values.status === 'present' && !showLate ? attrs.onTouchEnd : undefined}
        >
          <div className={styles['long-press-status']} style={{ width: `${longPressProcess}%` }}></div>
          <div className={styles.name}>{attendee.name}</div>
          <div className={styles.tags}>
            {values.lateInMinutes && (
              <div className={classNames(styles.status, styles.late)}>
                <Chip value={t('classbook.lateBy', { time: values.lateInMinutes })} />
              </div>
            )}
            <div
              className={styles.status}
              onClick={(event) => {
                event.stopPropagation();
                handleStatusCycle();
              }}
            >
              {getStatus()}
            </div>
          </div>
        </div>

        <div className={optionsClasses}>
          <div className={styles['options-inner']}>
            {updating && <LazyLoader embedded className={styles.loader} />}
            {/* ABSENCE STATUS */}
            <div className={styles.option}>
              <div className={styles['option-header']}>
                <div
                  className={styles.text}
                  onClick={() =>
                    values.status === 'absent' || values.status === 'excused'
                      ? setPendingValues({ ...values, status: 'present' })
                      : setPendingValues({ ...values, status: 'absent' })
                  }
                >
                  {t('classbook.isAbsent')}
                </div>
                {values.status !== 'present' && !isPhone && renderExcusedCheckbox(styles['hide-on-phone'])}
                <Switch
                  name={`isAbsent-${attendee.uuid}`}
                  checked={values.status !== 'present'}
                  onChange={(value) => {
                    if (value.target.checked) {
                      setPendingValues({ ...values, status: 'absent' });
                      handleUpdate({
                        uuid: attendee.absence?.uuid,
                        status: 'absent',
                      });
                    } else {
                      setPendingValues({ ...values, status: 'present' });
                      handleUpdate({
                        uuid: attendee.absence?.uuid,
                        status: 'present',
                      });
                    }
                  }}
                />
              </div>
              <div
                className={classNames(styles['option-area'], styles['absence-status-options'], {
                  [styles['is-expanded']]: values.status !== 'present',
                })}
              >
                <div className={styles['options-inner']}>
                  {isPhone && renderExcusedCheckbox(styles['show-on-phone'])}
                  <Select
                    isSearchable
                    options={reasons}
                    onChange={(value) => {
                      const option = value as SingleValue<SelectOptionType>;
                      handleUpdate({
                        ...attendee.absence,
                        absenceReason: option?.value as string | undefined,
                      });
                    }}
                    label={t('classbook.reason')}
                    isClearable
                    value={reasons.find((r) => r.value === values.absenceReason)}
                    name={`reason-${attendee.uuid}`}
                    menuPosition='fixed'
                  />
                </div>
              </div>
            </div>
            {/* LATE */}
            {values.status === 'present' && (
              <div className={styles.option}>
                <div className={styles['option-header']}>
                  <div className={styles.text} onClick={() => handleLateToggle(!showLate)}>
                    {t('classbook.late')}
                  </div>
                  <Switch
                    name={`late-${attendee.uuid}`}
                    checked={showLate}
                    onChange={(e) => handleLateToggle(e.target.checked)}
                  />
                </div>

                <div
                  className={classNames(styles['option-area'], styles['comment-options'], {
                    [styles['is-expanded']]: showLate,
                  })}
                >
                  <div className={styles['options-inner']}>
                    <Select
                      options={lateOptions}
                      onChange={(value) => {
                        const option = value as SingleValue<SelectOptionType>;

                        const lateInMinutes = option?.value && isNumber(option?.value) ? option.value : null;
                        handleUpdate({
                          ...attendee.absence,
                          lateInMinutes: lateInMinutes,
                        });
                        setPendingValues({ ...values, lateInMinutes });
                      }}
                      value={lateOptions.find((l) => l.value === values.lateInMinutes)}
                      name={`late-${attendee.uuid}`}
                      menuPosition='fixed'
                    />
                  </div>
                </div>
              </div>
            )}
            {/* COMMENT */}
            <div className={styles.option}>
              <div className={styles['option-header']}>
                <div className={styles.text} onClick={() => handleCommentToggle(!showComment)}>
                  {t('common.comment')}
                </div>
                <Switch
                  name={`commnet-${attendee.uuid}`}
                  checked={showComment}
                  onChange={(e) => handleCommentToggle(e.target.checked)}
                />
              </div>
              <div
                className={classNames(styles['option-area'], styles['comment-options'], {
                  [styles['is-expanded']]: showComment,
                })}
              >
                <div className={styles['options-inner']}>
                  <BpTipTap
                    key={attendee.uuid}
                    defaultValue={attendee.comment ?? ''}
                    onChange={(value) => setPendingValues({ ...values, comment: value })}
                    onBlur={() => handleUpdate({ ...attendee.absence, comment: values.comment })}
                    name={`comment-${attendee.uuid}`}
                    isDense
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showLateModal && (
        <Modal
          width={'s'}
          isOpen={showLateModal}
          hideCloseButton
          onRequestClose={() => setShowLateModal(false)}
          title={t('classbook.late')}
          onAfterOpen={() => {
            const defaultMinutes = calculateDefaultLateMinutes();
            setPendingValues({ ...values, lateInMinutes: defaultMinutes });
            handleUpdate({ ...attendee.absence, lateInMinutes: defaultMinutes });
            setShowLate(true);
          }}
        >
          <Select
            options={lateOptions}
            onChange={(value) => {
              const option = value as SingleValue<SelectOptionType>;
              const lateInMinutes = option?.value && isNumber(option?.value) ? option.value : null;
              handleUpdate({
                ...attendee.absence,
                lateInMinutes: lateInMinutes,
              });
              setPendingValues({ ...values, lateInMinutes });
              setShowLate(!!lateInMinutes);
              setShowLateModal(false);
            }}
            value={lateOptions.find((l) => l.value === values.lateInMinutes)}
            name={`late-modal-${attendee.uuid}`}
            menuPosition='fixed'
          />
          <ModalBottomButtons
            closeButton={{
              callback: () => {
                setShowLateModal(false);
                setShowLate(false);
                setPendingValues({ ...values, lateInMinutes: null });
                handleUpdate({ ...attendee.absence, lateInMinutes: null });
              },
            }}
            submitButton={{
              callback: () => {
                setShowLateModal(false);
              },
              text: t('common.confirm'),
            }}
            isLoading={false}
            errors={undefined}
          />
        </Modal>
      )}
    </>
  );
};
